import React, { useState } from 'react';
import { carteraApi } from './api';
import { Pill } from './helpers';

/**
 * Toggle Bot/Humano por conversación, en la cabecera del hilo.
 *
 * ── Qué hace y qué NO hace ───────────────────────────────────────────────
 * Cuando el bot escaló un lead a un humano (o alguien lo tomó), el hilo queda en
 * modo humano/pausado y el bot se calla para ESE lead. Este control lo devuelve
 * al bot con un clic: POST /bot-ventas/conversaciones/{id}/reactivar, que limpia
 * la pausa y pone modo=bot conservando cotización, budget_id y bitácora (§4.3:
 * "recuerda la negociación, olvida el enojo").
 *
 * ── Por qué SOLO en cotizaciones (§4.2) ──────────────────────────────────
 * 🔴 `conversacion.modo` es la MISMA columna que usa cobranza. Un toggle que
 * escriba ahí en un hilo de cobranza es la vía más directa a cruzar los dos
 * módulos. Por eso este control solo se pinta si el hilo es cotización
 * (`esCotizacion`), y el endpoint lo RE-VALIDA: responde 422 si el hilo no es
 * cotización. La defensa está en los dos lados a propósito.
 *
 * ── La regla que no se rompe (§5.2) ──────────────────────────────────────
 * 🔴 El control no adelanta el estado. Mientras el POST está en vuelo muestra un
 * spinner; el estado "vuelve al bot" solo se refleja cuando el backend confirma,
 * vía `onReactivado` que dispara el refresco de la lista con el dato real. Si el
 * POST falla, el hilo se queda como estaba y se muestra el error: nunca se pinta
 * "atendiendo" un bot que sigue mudo.
 *
 * @param {object} props
 * @param {object} props.conversacion  la fila seleccionada (necesita id)
 * @param {boolean} props.esCotizacion si el hilo es cotización (meta.tipo). El
 *        padre lo deriva de `bot_ventas != null`, que el backend solo llena en
 *        cotizaciones.
 * @param {boolean} props.pausado      si el bot está pausado para este lead
 *        (bot_ventas.pausado)
 * @param {string}  props.modo         'bot' | 'humano' | 'pausado'
 * @param {(resp: object) => (void|Promise<any>)} props.onReactivado
 *        callback tras confirmar; el padre refresca la lista para traer el
 *        estado real del hilo.
 */
export const ModoHiloToggle = ({ conversacion, esCotizacion, pausado, modo, onReactivado }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // 🔴 Fuera de cotizaciones no existe. Es la primera línea de la separación con
  // cobranza: si no se pinta, no hay forma de tocar `modo` desde aquí.
  if (!esCotizacion) return null;
  if (!conversacion?.id) return null;

  // El bot "atiende" cuando NO está pausado y el modo no es humano. Si atiende,
  // no hay nada que reactivar: se muestra el estado y punto.
  const atendiendo = !pausado && modo !== 'humano' && modo !== 'pausado';

  const reactivar = async () => {
    if (busy || atendiendo) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await carteraApi.reactivarBotVentas(conversacion.id);
      // El backend confirmó. Recién ahora el padre refresca con el estado real;
      // no tocamos nada localmente para no adelantar la posición.
      if (onReactivado) await onReactivado(data);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: '4px 9px',
            color: atendiendo ? '#15803D' : '#7A2E0E',
            background: atendiendo ? '#E5F8EF' : '#FFF1E8',
          }}
        >
          {atendiendo ? 'Bot atendiendo' : (modo === 'humano' ? 'En manos de un humano' : 'Bot pausado')}
        </span>

        {/* El botón solo aparece cuando hay algo que reactivar. Reactivar un bot
            que ya atiende no significa nada. */}
        {!atendiendo && (
          <Pill
            icon={busy ? 'pi-spin pi-spinner' : 'pi-replay'}
            disabled={busy}
            title="Devuelve este lead al bot: limpia la pausa y lo pone en modo bot, conservando la cotización y el historial."
            onClick={reactivar}
            style={{ padding: '6px 11px', fontSize: 11 }}
          >
            {busy ? 'Reactivando…' : 'Reactivar bot'}
          </Pill>
        )}
      </div>

      {error && (
        <div role="alert" style={{ fontSize: 10.5, color: '#B42318', lineHeight: 1.3, maxWidth: 220, textAlign: 'right' }}>
          {error}
        </div>
      )}
    </div>
  );
};

/**
 * Mensaje legible. El 422 es el candado de §4.2 (hilo que no es cotización): no
 * debería pasar porque el control no se pinta ahí, pero si un estado viejo lo
 * dispara, el mensaje del backend lo explica.
 */
const mensajeDeError = (error) => {
  const respuesta = error?.response;
  if (respuesta?.data?.message) return respuesta.data.message;
  if (!respuesta) return 'Sin conexión. Revisa tu red y vuelve a intentar.';
  return 'No se pudo reactivar el bot para este hilo.';
};
